// ─────────────────────────────────────────────────────────────────────────────
// Background Sync Hook - Automatic Outbox Processing
// Ensures outbox events are processed automatically in both development and production
// This hook is specifically designed to work reliably in Electron production builds
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';
import { drainOutboxOnce, forceSyncNow, setAxiosClient } from '../sync/syncService';
import { getOutboxStats, resetAllFailedRecords } from '../sync/outbox';
import networkService from '../hooks/services/networkService';
import { useAuthState } from './providers/AuthProvider';
import { useAxiosClient } from './providers/ApiProvider';
import { productionLogger } from '../services/productionLoggingService';

export interface BackgroundSyncOptions {
  intervalMs?: number;
  enabled?: boolean;
  enableLogging?: boolean;
  maxAttempts?: number;
  batchSize?: number;
  autoStartOnAuth?: boolean;
}

export interface BackgroundSyncStatus {
  isRunning: boolean;
  lastSync: string | null;
  pendingEvents: number;
  totalProcessed: number;
  errorCount: number;
}

/**
 * Background sync hook that ensures outbox events are processed automatically
 * Works in both development and production Electron builds
 */
export function useBackgroundSync(options: BackgroundSyncOptions = {}) {
  const {
    intervalMs = 30000, // 30 seconds
    enabled = true,
    enableLogging = true,
    maxAttempts = 10,
    batchSize = 25,
    autoStartOnAuth = true
  } = options;

  const authState = useAuthState();
  const axiosClient = useAxiosClient();
  const [status, setStatus] = useState<BackgroundSyncStatus>({
    isRunning: false,
    lastSync: null,
    pendingEvents: 0,
    totalProcessed: 0,
    errorCount: 0
  });

  // Provide axios client to sync service so it can call the API from background context
  useEffect(() => {
    if (axiosClient) {
      setAxiosClient(axiosClient);
      if (enableLogging) {
        console.log('🔧 Sync service axios client initialized from ApiProvider');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axiosClient]);

  // Use refs to track intervals and cleanup functions
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const statusIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const networkCleanupRef = useRef<(() => void) | null>(null);

  // Sync configuration
  const syncConfig = {
    intervalMs,
    batchSize,
    maxAttempts,
    enableLogging,
    enabled
  };

  // Update status function
  const updateStatus = async () => {
    try {
      const outboxStats = await getOutboxStats();
      setStatus(prev => ({
        ...prev,
        pendingEvents: outboxStats.total,
        lastSync: prev.lastSync // Keep existing lastSync
      }));
    } catch (error) {
      if (enableLogging) {
        console.error('Failed to update background sync status:', error);
      }
    }
  };

  // Process outbox function with enhanced error handling
  const processOutbox = async () => {
    try {
      const result = await drainOutboxOnce(syncConfig);
      
      if (result.processed > 0) {
        setStatus(prev => ({
          ...prev,
          lastSync: new Date().toISOString(),
          totalProcessed: prev.totalProcessed + result.processed,
          errorCount: prev.errorCount + result.failed
        }));

        if (enableLogging) {
          console.log(`🔄 Background sync processed: ${result.processed} total, ${result.succeeded} succeeded, ${result.failed} failed`);
        }

        // Log to production logger for monitoring
        productionLogger.info('Background sync completed', {
          context: 'background_sync_process',
          processed: result.processed,
          succeeded: result.succeeded,
          failed: result.failed,
          timestamp: new Date().toISOString()
        });
      }
    } catch (error) {
      setStatus(prev => ({
        ...prev,
        errorCount: prev.errorCount + 1
      }));

      if (enableLogging) {
        console.error('Background sync failed:', error);
      }

      // Log errors to production logger
      productionLogger.error('Background sync error', {
        context: 'background_sync_error',
        errorDetails: {
          name: error instanceof Error ? error.name : 'Unknown',
          message: error instanceof Error ? error.message : 'Unknown error'
        }
      });
    }
  };

  // Start background sync
  const startBackgroundSync = () => {
    if (syncIntervalRef.current || !enabled || !authState.isAuthenticated) {
      return;
    }

    if (enableLogging) {
      console.log(`🚀 Starting background sync with ${intervalMs}ms interval`);
    }

    setStatus(prev => ({ ...prev, isRunning: true }));

    // Initial sync attempt
    processOutbox();

    // Set up periodic sync
    syncIntervalRef.current = setInterval(processOutbox, intervalMs);

    // Set up status updates
    statusIntervalRef.current = setInterval(updateStatus, 10000); // Every 10 seconds

    // Set up network monitoring for production reliability
    setupNetworkMonitoring();

    // Log startup to production logger
    productionLogger.info('Background sync started', {
      context: 'background_sync_start',
      intervalMs,
      batchSize,
      maxAttempts
    });
  };

  // Stop background sync
  const stopBackgroundSync = () => {
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }

    if (statusIntervalRef.current) {
      clearInterval(statusIntervalRef.current);
      statusIntervalRef.current = null;
    }

    if (networkCleanupRef.current) {
      networkCleanupRef.current();
      networkCleanupRef.current = null;
    }

    setStatus(prev => ({ ...prev, isRunning: false }));

    if (enableLogging) {
      console.log('🛑 Background sync stopped');
    }

    // Log shutdown to production logger
    productionLogger.info('Background sync stopped', {
      context: 'background_sync_stop'
    });
  };

  // Enhanced network monitoring for production builds
  const setupNetworkMonitoring = () => {
    // Clean up any existing network monitoring
    if (networkCleanupRef.current) {
      networkCleanupRef.current();
    }

    // Monitor network online events
    const onlineHandler = async () => {
      // Reset any records that got stuck (exhausted retries) so they get another chance
      try {
        const resetCount = await resetAllFailedRecords(maxAttempts);
        if (resetCount > 0 && enableLogging) {
          console.log(`🔄 Reset ${resetCount} previously-failed outbox record(s) for retry`);
        }
      } catch (resetError) {
        if (enableLogging) {
          console.warn('Failed to reset failed outbox records:', resetError);
        }
      }

      // Force immediate sync when coming online
      try {
        await forceSyncNow(syncConfig);
      } catch (error) {
        if (enableLogging) {
          console.error('Failed to sync after coming online:', error);
        }
      }
    };

    // Multiple fallback strategies for network detection
    
    // 1. Use networkService (preferred)
    const networkServiceCleanup = networkService.onOnline(onlineHandler);
    
    // 2. Browser online event (fallback for production)
    const browserOnlineHandler = () => {
      if (enableLogging) {
        console.log('🌐 Browser online event - triggering sync');
      }
      onlineHandler();
    };
    
    window.addEventListener('online', browserOnlineHandler);
    
    // 3. Visibility change (app comes back to foreground)
    const visibilityHandler = () => {
      if (!document.hidden && navigator.onLine) {
        if (enableLogging) {
          console.log('👁️ App visible and online - triggering sync');
        }
        onlineHandler();
      }
    };
    
    document.addEventListener('visibilitychange', visibilityHandler);

    // Combined cleanup function
    networkCleanupRef.current = () => {
      if (networkServiceCleanup) {
        networkServiceCleanup();
      }
      window.removeEventListener('online', browserOnlineHandler);
      document.removeEventListener('visibilitychange', visibilityHandler);
    };
  };

  // Auto-start when authenticated (only after axios client is available)
  useEffect(() => {
    if (autoStartOnAuth && authState.isAuthenticated && enabled && axiosClient) {
      // Ensure sync service has the axios client before starting
      setAxiosClient(axiosClient);
      startBackgroundSync();
    }

    return () => {
      stopBackgroundSync();
    };
  }, [authState.isAuthenticated, autoStartOnAuth, enabled, axiosClient]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopBackgroundSync();
    };
  }, []);

  // Manual sync function
  const triggerManualSync = async () => {
    try {
      const result = await forceSyncNow(syncConfig);
      await updateStatus();
      return result;
    } catch (error) {
      if (enableLogging) {
        console.error('Manual sync failed:', error);
      }
      throw error;
    }
  };

  return {
    status,
    startBackgroundSync,
    stopBackgroundSync,
    triggerManualSync,
    updateStatus
  };
}

export default useBackgroundSync;
